import React, { useEffect, useState } from 'react';
import { getContent } from '@/lib/api';
import { useSite } from '@/context/SiteContext';
import { Seo } from '@/components/Seo';
import { Hero } from '@/components/home/Hero';
import { Metrics } from '@/components/home/Metrics';
import { Intro } from '@/components/home/Intro';
import { Capabilities } from '@/components/home/Capabilities';
import { ServicesPreview } from '@/components/home/ServicesPreview';
import { EquipmentExperience } from '@/components/home/EquipmentExperience';
import { ProjectsShowcase } from '@/components/home/ProjectsShowcase';
import { MapSection } from '@/components/home/MapSection';
import { LiveProjects } from '@/components/LiveProjects';
import { Vision } from '@/components/home/Vision';
import { CtaSection } from '@/components/CtaSection';
import { ClientsMarquee } from '@/components/ClientsMarquee';
import { SectionHeading } from '@/components/SectionHeading';
import { EnquiryForm } from '@/components/EnquiryForm';
import { Phone, Mail, MapPin } from 'lucide-react';

export default function Home() {
  const { hero, site } = useSite();
  const [data, setData] = useState({ metrics: [], services: [], equipment: [], projects: [], locations: [], about: null });

  useEffect(() => {
    Promise.all([
      getContent('metrics'),
      getContent('services'),
      getContent('equipment'),
      getContent('projects'),
      getContent('locations'),
      getContent('about'),
    ])
      .then(([metrics, services, equipment, projects, locations, about]) =>
        setData({ metrics, services, equipment, projects, locations, about })
      )
      .catch(() => {});
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Sri Lakshmi Enterprises',
    alternateName: 'Sri Lakshmi Earth Movers',
    url: 'https://www.sle.enterprises',
    description: site?.description,
    foundingDate: '1970',
    email: site?.email,
    telephone: site?.phones,
    address: [
      { '@type': 'PostalAddress', name: 'Head Office', streetAddress: site?.headOffice?.address, addressRegion: 'Tamil Nadu', addressCountry: 'IN' },
      { '@type': 'PostalAddress', name: 'Branch Office', streetAddress: site?.branchOffice?.address, addressRegion: 'Tamil Nadu', addressCountry: 'IN' },
    ],
  };

  return (
    <>
      <Seo
        title={null}
        description={site?.description || 'Heavy engineering, earthmoving and contracting across Tamil Nadu since 1970.'}
        path="/"
        image={hero?.poster}
        jsonLd={jsonLd}
      />
      <Hero hero={hero} />
      <Metrics metrics={data.metrics} />
      <Intro about={data.about} />
      <Capabilities capabilities={site?.capabilities || []} />
      <ServicesPreview services={data.services} />
      <EquipmentExperience equipment={data.equipment} />
      <LiveProjects projects={data.projects} />
      <ProjectsShowcase projects={data.projects} />
      <MapSection locations={data.locations} />

      {/* Clients */}
      <section className="bg-[#F6F5F1] py-20 lg:py-28">
        <div className="container-x">
          <SectionHeading eyebrow="Our Valuable Clients" title="Trusted across projects that matter." align="center" className="mx-auto" />
        </div>
        <div className="mt-14">
          <ClientsMarquee dark={false} />
        </div>
      </section>

      <Vision about={data.about} />
      <CtaSection />

      {/* Contact */}
      <section className="bg-[#F6F5F1] py-20 lg:py-28" id="contact">
        <div className="container-x grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Contact" title="Let's move your project forward." />
            <div className="mt-8 space-y-6">
              {(site?.offices || []).map((o) => (
                <div key={o.label + o.city} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-[#075B61]/8 text-[#075B61]"><MapPin size={17} /></span>
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#20272A]/40">{o.label} — {o.city}</div>
                    <div className="text-sm text-[#20272A]/75">{o.address}</div>
                  </div>
                </div>
              ))}
              <div className="flex flex-wrap gap-4 pt-2">
                {site?.phones?.map((p) => (
                  <a key={p} href={`tel:${p.replace(/\s/g, '')}`} className="inline-flex items-center gap-2 text-sm text-[#20272A]/75 hover:text-[#075B61]">
                    <Phone size={15} className="text-[#B88A3B]" /> {p}
                  </a>
                ))}
              </div>
              {site?.email && (
                <a href={`mailto:${site.email}`} className="inline-flex items-center gap-2 text-sm text-[#20272A]/75 hover:text-[#075B61]">
                  <Mail size={15} className="text-[#B88A3B]" /> {site.email}
                </a>
              )}
            </div>
          </div>
          <div className="lg:col-span-7">
            <EnquiryForm />
          </div>
        </div>
      </section>
    </>
  );
}
